import { useEffect, useMemo } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@inkblot-therapy/squid"
import { useSectionState } from "../../context/SectionContextProvider"
import { useGetFormByProvince } from "../../hooks/useGetFormByProvince"
import { FormSection } from "../../types/FormSection"
import { Section } from "../../types/SectionState"
import { AlertSection } from "../AlertSection"
import { SubHeader } from "../SubHeader"

interface Props {
  hideEditButtons?: boolean
  hideHeaderText?: boolean
}

export function FormReviewPage({ hideEditButtons, hideHeaderText }: Props) {
  const navigate = useNavigate()
  const sectionState = useSectionState()
  const { province } = useParams<{ step: string; province: string }>()
  const {
    formState: { errors },
    trigger,
  } = useFormContext()
  const { data } = useGetFormByProvince(province ?? "ontario")
  const methods = useFormContext()

  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()

  useEffect(() => {
    trigger()
  }, [trigger, resolvedLanguage])

  const sections = useMemo(
    () =>
      Object.values(sectionState).filter(
        ({ name }) => name !== t("client.sideNav.review"),
      ),
    [sectionState, t],
  )

  const handleEditClick = (stepNumber: number) => {
    navigate(`../${stepNumber}`)
  }

  const getSectionData = (order: number = 0, data?: FormSection[]) => {
    return data?.[order - 1]
  }
  const skipConditionalSection = (section: Section) => {
    const sectionData = getSectionData(section.order, data)
    if (sectionData) {
      const { conditional_section_mapped_field, conditional_section_values } =
        sectionData
      if (conditional_section_mapped_field && conditional_section_values) {
        const values = methods.getValues(conditional_section_mapped_field)
        if (
          conditional_section_values &&
          !conditional_section_values.includes(values)
        )
          return true
      }
    }
    return false
  }

  return (
    <div className="flex flex-col mb-16">
      {!hideHeaderText ? (
        <div className="flex flex-col">
          <h2 className="mb-3 text-2xl [font-size:var(--alias-font-display-xl-font-size)] [font-family:var(--alias-font-display-xl-font-family)] pb-[var(--alias-padding-base)]">
            {t("client.review.headings.title")}
          </h2>
          <p className="[font-size:var(--alias-font-subtitle-lg-font-size)] text-[color:var(--alias-color-text-weaker)] [font-family:var(--alias-font-subtitle-lg-font-weight)] mb-8">
            {t("client.review.headings.subtitle")}
          </p>
        </div>
      ) : null}
      {sections.map((section, index) => {
        if (skipConditionalSection(section)) return <></>
        return (
          <div className="flex w-full" key={section.name + index}>
            <div className="flex flex-col mb-8 w-full mr-3">
              <div className="border-b-[1px] border-[color:var(--alias-color-border-weak)] my-2 w-full">
                <div className="flex pb-5 items-center justify-between">
                  <h2
                    aria-label={`${section.name} section`}
                    className="[font-family:var(--alias-font-display-md-font-family)] [font-size:var(--alias-font-display-md-font-size)] text-[color:var(--alias-color-text-base)] font-bold"
                  >
                    {section.name}
                  </h2>
                  {!hideEditButtons ? (
                    <Button
                      className="editButton"
                      variant="outlined"
                      onClick={() => handleEditClick(section.order)}
                    >
                      {t("client.review.buttons.edit")}
                    </Button>
                  ) : null}
                </div>
              </div>
              <AlertSection
                errors={section.questions
                  .map(({ fieldName }) => errors[fieldName]?.message)
                  .filter(Boolean)}
                isIncomplete={!section.completed}
              />
              <div className="flex flex-wrap">
                {section.questions
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((question) => {
                    if (question.inputName === "label") {
                      return (
                        <div
                          className="flex flex-col my-8 text-md w-full [font-size:var(--alias-font-label-strong-font-size)]"
                          key={question.fieldName}
                        >
                          <SubHeader label={question.label || ""} />
                        </div>
                      )
                    }
                    return (
                      <div
                        className="flex flex-col my-8 text-md w-1/2 [font-size:var(--alias-font-label-strong-font-size)]"
                        key={question.fieldName}
                      >
                        <p className="font-bold [font-family:var(--alias-font-label-strong-font-family)] text-[color:var(--alias-color-text-weaker)] mb-3">
                          {question.label}
                        </p>
                        <p
                          className={`[font-family:var(--alias-font-label-weak-font-family)] text-[color:var(--alias-color-text-base)] break-words
                            ${
                              question.displayValue?.includes("\r\n")
                                ? "whitespace-pre-line"
                                : ""
                            }`}
                        >
                          {question.displayValue || "--"}
                        </p>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
